import { ingredientPriceCents, type BuilderIngredient } from './pizza-builder'

/**
 * Extra toppings on a pizza **off the menu** — `FRONTEND.md` §9.41.
 *
 * Not the custom-pizza builder (§9.25), which composes a pizza from nothing. This is the smaller and
 * far more common thing: a *Margherita* from the catalogue, with ham on it.
 *
 * **Moved here from `manager-orders` on 2026-09-03**, unchanged, so the customer app can use it too:
 * §9.168 gives the customer menu the same dialog and neither app may import the other's module. The
 * manager keeps a re-export at the old path, so nothing it owns had to move with it.
 *
 * Ported from legacy `pizza-extras-modal.tsx`, and its text format is kept to the character because
 * **legacy re-reads its own string** to reopen the dialog with the right quantities. Every order in
 * the dumps carries that format, so a tidier one would mean an order taken last week could not be
 * reopened.
 */

/** How many of one topping: none, one, or a double portion. Legacy cycles 0 → 1 → 2 → 0. */
export type ExtraQty = 1 | 2

export type ChosenExtras = ReadonlyMap<number, ExtraQty>

/** Legacy's cycle. A double portion of ham is an ordinary order, so this is not a checkbox. */
export function cycleExtra(chosen: ChosenExtras, id: number): Map<number, ExtraQty> {
  const next = new Map(chosen)
  const current = next.get(id)
  if (current === undefined) next.set(id, 1)
  else if (current === 1) next.set(id, 2)
  else next.delete(id)
  return next
}

/**
 * The line's topping text — `+Šunka, Niva 2×`.
 *
 * In the ingredients' own order rather than the order they were tapped, so the same pizza reads the
 * same way twice and a kitchen ticket does not depend on which topping somebody pressed first.
 */
export function extrasText(
  chosen: ChosenExtras,
  ingredients: readonly BuilderIngredient[],
): string {
  return ingredients
    .filter((ingredient) => chosen.has(ingredient.id))
    .map((ingredient) =>
      chosen.get(ingredient.id) === 2 ? `${ingredient.name} 2×` : `+${ingredient.name}`,
    )
    .join(', ')
}

/** What the toppings add, at this pizza's size. A double portion costs twice. */
export function extrasCents(
  chosen: ChosenExtras,
  ingredients: readonly BuilderIngredient[],
  sizeCm: number,
): number {
  let total = 0
  for (const ingredient of ingredients) {
    const qty = chosen.get(ingredient.id)
    if (qty === undefined) continue
    total += ingredientPriceCents(ingredient, sizeCm) * qty
  }
  return total
}

/**
 * Reads a line's existing text back into quantities, so reopening shows what was chosen.
 *
 * Legacy's parse, including its order: `Name 2×` is checked **before** `+Name`, because a double
 * portion's text contains neither the `+` nor anything the single form would match — and checking
 * the other way round on a different format would silently downgrade every double to a single.
 */
export function parseExtras(
  text: string | undefined,
  ingredients: readonly BuilderIngredient[],
): Map<number, ExtraQty> {
  const chosen = new Map<number, ExtraQty>()
  if (!text) return chosen
  for (const ingredient of ingredients) {
    if (text.includes(`${ingredient.name} 2×`)) chosen.set(ingredient.id, 2)
    else if (text.includes(`+${ingredient.name}`)) chosen.set(ingredient.id, 1)
  }
  return chosen
}

/**
 * Whether a catalogue line takes toppings.
 *
 * By category name, which is what legacy and the customer app (`HomePage.tsx:184`) both do. A
 * franchise renaming *Pizza* to *Pizze* keeps working; one calling it something with no *pizza* in it
 * would not, and that is the same limit the customer menu already lives with rather than a new one.
 */
export function isPizzaCategory(categoryName: string | undefined | null): boolean {
  return typeof categoryName === 'string' && categoryName.toLowerCase().includes('pizza')
}
